import { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { GcpCloudMonitoringIntegrationFormUpdateIntegrationMutation } from "./__generated__/GcpCloudMonitoringIntegrationFormUpdateIntegrationMutation.graphql";
import { GcpCloudMonitoringIntegrationFormValidateIntegrationMutation } from "./__generated__/GcpCloudMonitoringIntegrationFormValidateIntegrationMutation.graphql";
import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";

const updateIntegrationMutation = graphql`
  mutation GcpCloudMonitoringIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation GcpCloudMonitoringIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

interface GcpCloudMonitoringIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
}

export default function GcpCloudMonitoringIntegrationForm({
  onComplete,
  onCancel
}: GcpCloudMonitoringIntegrationFormProps) {
  const [serviceAccountEmail, setServiceAccountEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<GcpCloudMonitoringIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<GcpCloudMonitoringIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    if (!serviceAccountEmail.trim()) {
      setFormError("Service Account Email is required");
      return;
    }
    const credentials = {
      service_account_email: serviceAccountEmail.trim(),
    };

    const input = {
      integrationType: "GCP_CLOUD_MONITORING" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          return;
        }

        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }

        setValidationSuccess("Credentials validated successfully!");
        setIsValidated(true);
      },
      onError: error => {
        setFormError(error.message);
      }
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    if (!serviceAccountEmail.trim()) {
      setFormError("Service Account Email is required");
      return;
    }
    const credentials = {
      service_account_email: serviceAccountEmail.trim(),
    };

    const input = {
      integrationType: "GCP_CLOUD_MONITORING" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          onComplete(false);
          return;
        }

        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }

        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">Service Account Configuration</h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service-account-email" className="text-muted-foreground">Service Account Email</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="service-account-email"
                    type="text"
                    value={serviceAccountEmail}
                    onChange={(e) => {
                      setServiceAccountEmail(e.target.value);
                      setValidationSuccess(null);
                      setIsValidated(false);
                    }}
                    placeholder="e.g., <service-account-name>@<project-name>.iam.gserviceaccount.com"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

          </div>
        </div>
      </div>

      {formError && (
        <ErrorAlert icon="none">
          {formError}
        </ErrorAlert>
      )}

      {validationSuccess && (
        <SuccessAlert icon="none">
          {validationSuccess}
        </SuccessAlert>
      )}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : (!isValidated ? "Please test the connection first" : null)}
          position="top"
        >
          <span className="inline-block">
            <Button
              onClick={handleSave}
              disabled={isMutationInFlight || isValidationInFlight || !isValidated || !canModify}
            >
              {isMutationInFlight ? "Saving..." : "Connect"}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
